<template>
  <draggable
    :class="['nested-list', { 'nested-list_empty': !realValue.length }]"
    ghost-class="nested-list__ghost"
    chosen-class="nested-list__chosen"
    :tag="tag"
    :list="list"
    :value="value"
    :group="group"
    :component-data="getComponentData()"
    v-on="$listeners"
    @input="emitter"
  >
    <template v-for="(item, i) in realValue">
      <template v-if="getProp(item, childProp)">
        <v-list-group
          :key="`${nesting}-${i}`"
          :value="getProp(item, openProp) || true"
          :class="[
            'nested-list__group',
            {
              'nested-list__group_empty': !getProp(item, childProp, []).length
            }
          ]"
          no-action
        >
          <template v-slot:activator>
            <v-list-item-icon v-if="getProp(item, iconProp)">
              <slot name="parent-icon" :item="item">
                <v-icon>{{ getProp(item, iconProp) }}</v-icon>
              </slot>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                <slot name="parent-title" :item="item">
                  {{ getProp(item, nameProp) }}
                </slot>
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon @click.stop="$emit('edit-tile', item)">
                <slot name="parent-edit-button" :item="item">
                  <v-icon>mdi-square-edit-outline</v-icon>
                </slot>
              </v-btn>
            </v-list-item-action>
            <v-list-item-action>
              <v-btn
                icon
                :disabled="!!getProp(item, childProp).length"
                @click.stop="$emit('remove-tile', item)"
              >
                <slot name="parent-delete-button" :item="item">
                  <v-icon>mdi-delete</v-icon>
                </slot>
              </v-btn>
            </v-list-item-action>
          </template>
          <nested-draggable
            :data-parent-id="getProp(item, idProp)"
            :parent-id="getProp(item, idProp)"
            :nesting="nesting + 1"
            :list="getProp(item, childProp)"
            v-bind="{ ...excludeProp($props, 'value'), ...$attrs }"
            v-on="$listeners"
          >
            <template
              v-for="slot in Object.keys($scopedSlots)"
              v-slot:[slot]="scope"
            >
              <slot :name="slot" v-bind="scope" />
            </template>
          </nested-draggable>
        </v-list-group>
      </template>
      <template v-else>
        <v-list-item :key="`${nesting}-${i}`" link>
          <v-list-item-icon v-if="getProp(item, iconProp)">
            <slot name="icon" :item="item">
              <v-icon>{{ getProp(item, iconProp) }}</v-icon>
            </slot>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              <slot name="title" :item="item">
                {{ getProp(item, nameProp) }}
              </slot>
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action>
            <v-btn icon @click="$emit('edit-tile', item)">
              <slot name="edit-button" :item="item">
                <v-icon>mdi-square-edit-outline</v-icon>
              </slot>
            </v-btn>
          </v-list-item-action>
          <v-list-item-action>
            <v-btn icon @click="$emit('remove-tile', item)">
              <slot name="remove-button" :item="item">
                <v-icon>mdi-delete</v-icon>
              </slot>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </template>
    </template>
  </draggable>
</template>

<script>
import { get, omit } from 'lodash';
import draggable from 'vuedraggable';

export default {
  name: 'NestedDraggable',

  components: {
    draggable
  },

  props: {
    value: {
      required: false,
      type: Array,
      default: null
    },
    list: {
      required: false,
      type: Array,
      default: null
    },
    tag: {
      type: String,
      default: 'v-list'
    },
    group: {
      type: Object,
      default: () => ({
        name: 'nested'
      })
    },
    nesting: {
      type: Number,
      default: 0
    },
    parentId: {
      type: Number,
      default: 0
    },
    idProp: {
      type: String,
      default: 'id'
    },
    nameProp: {
      type: String,
      default: 'name'
    },
    iconProp: {
      type: String,
      default: 'default_icon'
    },
    childProp: {
      type: String,
      default: 'children'
    },
    openProp: {
      type: String,
      default: 'open'
    }
  },

  computed: {
    // this.value when input = v-model
    // this.list  when input != v-model
    realValue() {
      return this.value ? this.value : this.list;
    }
  },

  methods: {
    getProp: get,
    excludeProp: omit,
    getComponentData() {
      return {
        on: {},
        attrs: this.$attrs,
        props: this.$props
      };
    },
    emitter(value) {
      this.$emit('input', value);
    }
  }
};
</script>
<style lang="scss">
@import '~vuetify/src/styles/styles.sass';

.nested-list {
  & & {
    padding: 0 0 0 40px;
  }

  .v-list-item {
    cursor: grabbing;
    border-width: 1px;
    border-style: solid;
    border-color: transparent;

    &.theme--light {
      border-color: map-deep-get($material-light, 'dividers');
    }

    &.theme--dark {
      border-color: map-deep-get($material-dark, 'dividers');
    }

    &__title {
      display: flex;
    }
  }

  .v-list-item,
  .v-list-group {
    + .v-list-item,
    + .v-list-group > .v-list-item {
      border-top-width: 0;
    }
  }

  .v-list-group {
    &.v-list-group--active:not(.nested-list__group_empty) {
      + .v-list-item,
      + .v-list-group > .v-list-item {
        border-top-width: 1px;
      }
    }

    [data-parent-id] .v-list-item:first-child {
      border-top-width: 0;
    }

    &:not(:last-child) .v-list-item:last-child {
      border-bottom-width: 0;
    }
  }

  &__ghost {
    box-shadow: inset 0 0 0 2px var(--v-primary-base);
  }

  &__chosen {
  }
}
</style>
